import { ErrorBoundary } from 'components/common/error-boundary';
import { FlexTableWrapper } from 'components/common/layout/flex-table-wrapper';
import { SessionHitsHeader } from 'components/sections/analytics/session-hits/header';
import { SessionHitsTable } from 'components/sections/analytics/session-hits/table';
import useAnalyticsStore from 'components/sections/analytics/store/use-analytics-store';
import { HittersContext } from 'contexts/hitters.context';
import { MachineContext } from 'contexts/machine.context';
import { t } from 'i18next';
import { IVerboseComponent } from 'interfaces/i-components';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useContext, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { paths } from 'routes/paths';

export const AnalyticsSessionHits = (props: IVerboseComponent) => {
  const navigate = useNavigate();
  const { hitters } = useContext(HittersContext);

  const { hitterID = '', sessionID = '' } = useParams<{
    hitterID: string;
    sessionID: string;
  }>();

  const { machine } = useContext(MachineContext);

  // we'll need to grab the session info to show in the header
  useEffect(() => {
    if (!machine.machineID) {
      return;
    }

    useAnalyticsStore.getState().fetchSessions({
      machineID: machine.machineID,
      sessionID,
      // we may not have a hitter ID when we're showing anonymous sessions - passing in null will specifically return the no-hitter ID session summary
      hitterID: hitterID || null,
      limit: 1,
    });
  }, [sessionID, hitterID, machine.machineID]);

  const hitter = useMemo(
    () => hitters.find((h) => h._id === hitterID),
    [hitters, hitterID]
  );

  return (
    <ErrorBoundary identifier={props.identifier}>
      <FlexTableWrapper
        gap={RADIX.FLEX.GAP.SECTION}
        header={
          <SessionHitsHeader
            sessionID={sessionID}
            hitterID={hitterID}
            breadcrumbs={[
              {
                label: 'main.hitting-reports',
                onClick: () => navigate(paths.analytics.hittingReports),
              },
              {
                label: hitter?.name ?? t('hitters.anonymous'),
              },
            ]}
            onDeleteSession={() => navigate(paths.analytics.hittingReports)}
          />
        }
        table={
          <SessionHitsTable
            identifier="AnalyticsHitterSessionTable"
            sessionID={sessionID}
            hitterID={hitterID}
            machineID={machine.machineID}
          />
        }
      />
    </ErrorBoundary>
  );
};
